import { useQuery } from "@apollo/client";
import { Classes } from "@blueprintjs/core";
import { useNavigate } from "react-router-dom";

import {
  allowBuyCredits,
  isInWorkspace,
} from "@/components/helpers/workspace/permissions";
import { Box } from "@/components/layout/flexbox";
import PaymentForm from "@/components/pieces/buy-credits/PaymentForm";
import usePendingOrder from "@/components/pieces/buy-credits/usePendingOrder";
import FaqCategory from "@/components/pieces/faq/FaqCategory";
import PrettyBox from "@/components/pieces/PrettyBox";
import ProductDetails from "@/components/pieces/products/ProductDetails";
import ProductSelector from "@/components/pieces/products/ProductSelector";
import { CreditPackagesDocument } from "@/graphql";

function PageLoader() {
  return (
    <>
      <Box fontSize={22} fontWeight={600} mb={3}>
        Select number of credits
      </Box>
      <ProductSelector loading={true} />
      <Box className={Classes.SKELETON} mt={4} height={400} />
    </>
  );
}

function BuyCreditsForm({
  customerId,
  loading,
  products,
  defaultProductQuantities,
}) {
  const navigate = useNavigate();
  const prodIds = products.map((p) => p.id).join(",");
  const [order, { syncing, setItems, setOrder }] = usePendingOrder({
    customerId,
    key: `credit-order-${prodIds}`,
    defaultProductQuantities,
  });
  const selectedProduct =
    order &&
    order.orderItems &&
    !!order.orderItems.length &&
    order.orderItems[0].product;

  function onOrderConfirmed(orderId) {
    if (customerId) {
      navigate(`/client/${customerId}/buy/success/${orderId}`);
    } else {
      navigate(`/buy/success/${orderId}`);
    }
  }

  if (!order) return <PageLoader />;

  return (
    <>
      <PrettyBox>
        <Box fontSize={22} fontWeight={600} mb={3}>
          Select number of credits
        </Box>
        <ProductSelector
          loading={loading}
          disabled={syncing}
          products={products}
          selectedProduct={selectedProduct}
          setSelectedProduct={(product) => {
            setItems({
              [product.id]: 1,
            });
          }}
        />
        {selectedProduct ? <ProductDetails product={selectedProduct} /> : null}
      </PrettyBox>
      <Box mt={4}>
        <PaymentForm
          order={order}
          onOrderConfirmed={onOrderConfirmed}
          setOrder={setOrder}
          customerId={customerId}
          ctaVerb="Buy Credits"
          allowBuyCredits={allowBuyCredits()}
        />
      </Box>
      <Box fontSize={22} fontWeight={600} mb={3} pt={5} mt={5} className="bts">
        Common Questions
      </Box>
      <FaqCategory
        category={
          isInWorkspace() ? "Credit System" : "Credit System for Individuals"
        }
      />
    </>
  );
}

export default function BuyCreditsFormWrapper({
  productSlug,
  customerId = undefined,
}) {
  const navigate = useNavigate();
  const { data, loading } = useQuery(CreditPackagesDocument, {
    variables: {
      slug: productSlug,
    },
  });
  const products =
    data &&
    data.creditPackages.filter(
      (item) =>
        // Hide intro product and First Session Special
        !item.introProduct && item.slug !== "intro-offer",
    );
  const numProds = products && products.length;
  const defaultProductQuantities = numProds
    ? {
        [products[numProds === 1 ? 0 : 1].id]: 1,
      }
    : null;
  if (!defaultProductQuantities) {
    return <PageLoader />;
  }

  // If a user tries to go to a product-specific page but we don't have a product
  // for that slug, just redirect them to the main purchase screen
  if (!numProds && productSlug) {
    if (customerId) {
      navigate(`/client/${customerId}/buy`);
    } else {
      navigate(`/buy`);
    }
    return null;
  }

  return (
    <BuyCreditsForm
      loading={loading}
      customerId={customerId}
      products={products}
      defaultProductQuantities={defaultProductQuantities}
    />
  );
}
